import { CardContent } from '@mui/material'
import React from 'react'
import { FormatDateTime } from 'src/date'
import { ViewOnlyForm } from 'src/form'
import { PatientHistoryType } from 'src/graphql.generated'
import { ExpandableCard } from 'src/designSystem'
import { CardTitle, Dash } from 'src/legacy/components/mui'
import MeruDate from 'src/legacy/lib/MeruDate'
import { checkIfKaiserHealthPlan } from 'src/participant/participantUtils'
import IntakeNoteCoachingFields from 'src/participantHistory/IntakeNoteCoachingFields'
import FinancialEligibilityHealthPlanFields, {
  getInitialValuesFromFinancialEligibilityPatientHistoryEntry,
} from 'src/participantHistory/financialEligibility/FinancialEligibilityHealthPlanFields'
import { IntakeNoteComprehensiveFields } from 'src/participantHistoryForm/form/intakeNoteComprehensive/IntakeNoteComprehensiveFields'
import TreatmentPlanComprehensiveFields from 'src/participantHistoryForm/form/treatmentPlanComprehensive/TreatmentPlanComprehensiveFields'
import { CheckinCallNoteFields } from 'src/participantHistoryForm/form/checkinCall/CheckinCallNoteFields'
import AdvancedFollowUpNoteFields from 'src/participantHistoryForm/form/advancedFollowUpNote/AdvancedFollowUpNoteFields'
import BurnoutFields from '../BurnoutFields'
import CustomFields from '../CustomFields'
import GAD7Fields from '../GAD7Fields'
import MissedIntakeCallFields from '../MissedIntakeCallFields'
import NoteFields from '../NoteFields'
import PHQ9Fields from '../PHQ9Fields'
import PCL5Fields from '../PCL5Fields'
import ASRMFields from '../ASRMFields'
import OCIRFields from '../OCIRFields'
import ISIFields from '../ISIFields'
import ParticipantAcceptedFields from '../ParticipantAcceptedFields'
import ParticipantDeclinedFields from '../ParticipantDeclinedFields'
import ParticipantReEnrollmentTable from '../ParticipantReEnrollmentTable'
import ProgramEndFields from '../ProgramEndFields'
import TreatmentPlanFields from '../TreatmentPlanFields'
import VideoCallPatientHistoryFields from '../VideoCallPatientHistoryFields'
import WHO5Fields from '../WHO5Fields'
import WPAIFields from '../WPAIFields'
import { PATIENT_HISTORY_TYPE_NAMES } from '../constants'
import IntakeNoteFields from '../intakeNote/IntakeNoteFields'
import { PatientHistoryEntryFragment } from '../patientHistoryQueries.generated'
import ParticipantHistoryDelete from './ParticipantHistoryDelete'
import PatientHistoryFilesPreview from './ParticipantHistoryFilesPreview'
import Source from './Source'
import IntakeNotePsychiatryFields from '../IntakeNotePsychiatryFields'
import LabOrderFields, { getInitialValuesFromLabOrderPatientHistoryEntry } from '../LabOrderFields'
import DischargeSummaryNoteFields from '../DischargeSummaryNoteFields'
import { MonthlySummaryNoteFields } from '../MonthlySummaryNoteFields'

type TitleProps = {
  entry: PatientHistoryEntryFragment
  uid: string
}

const Title = ({ entry, uid }: TitleProps) => {
  const { createdAt, entryID, source, type } = entry

  return (
    <CardTitle action={<ParticipantHistoryDelete createdAt={createdAt} entryID={entryID} uid={uid} source={source} />}>
      {type ? PATIENT_HISTORY_TYPE_NAMES[type] : 'Unknown'}
    </CardTitle>
  )
}

const PATIENT_HISTORY_TYPES_WITH_ONLY_DESCRIPTION_FIELD: Set<PatientHistoryType> = new Set([
  PatientHistoryType.AnonymousNote,
  PatientHistoryType.Note,
  PatientHistoryType.PhoneCall,
  PatientHistoryType.Sms,
  PatientHistoryType.LabOrderNote,
])

type SubtitleProps = {
  entry: PatientHistoryEntryFragment
  programStartDate?: Date | null | undefined
}

const Subtitle = ({ entry, programStartDate }: SubtitleProps) => (
  <>
    <DateOfEvent entry={entry} programStartDate={programStartDate} />
    <Dash />
    <span>
      Submitted <Source coachID={entry.coachID} value={entry.source} /> on <FormatDateTime value={entry.createdAt} />
    </span>
  </>
)

type DateOfEventProps = {
  entry: PatientHistoryEntryFragment
  programStartDate?: Date | null | undefined
}

const DateOfEvent = ({ entry, programStartDate }: DateOfEventProps) => {
  const dateOfEvent = new MeruDate(entry.date).formatDate()
  const weekNumber = new MeruDate(entry.date).formatWeekNumber(programStartDate, true)
  return <span>Date of event: {weekNumber ? `${dateOfEvent} (week ${weekNumber})` : dateOfEvent}</span>
}

type PatientHistoryEntryProps = {
  entry: PatientHistoryEntryFragment
  uid: string
  programStartDate: Date | null | undefined
  credentialingHealthPlan?: string | null
}

const getInitialValuesFromPatientHistoryEntry = (entry: PatientHistoryEntryFragment) => {
  if (entry.__typename === 'FinancialEligibilityPatientHistory') {
    return getInitialValuesFromFinancialEligibilityPatientHistoryEntry(entry)
  }

  if (entry.__typename === 'LabOrderPatientHistory') {
    return getInitialValuesFromLabOrderPatientHistoryEntry(entry)
  }

  if (
    entry.type === PatientHistoryType.TreatmentPlan ||
    entry.type === PatientHistoryType.TreatmentPlanComprehensive ||
    entry.type === PatientHistoryType.TreatmentPlanComprehensiveUpdate ||
    entry.type === PatientHistoryType.IntakeNotePsychiatry ||
    entry.type === PatientHistoryType.IntakeNoteComprehensive ||
    entry.type === PatientHistoryType.DischargeSummaryNote ||
    entry.type === PatientHistoryType.MonthlySummaryNote ||
    entry.type === PatientHistoryType.CheckInCall ||
    entry.type === PatientHistoryType.AdvancedFollowUpNote
  ) {
    return entry.value ?? {}
  }

  return entry
}

// TODO 2020-11-27 -- INTAKE_NOTE version handling can be removed when access to old-style intake notes is not needed
const ParticipantHistoryEntry = ({
  entry,
  uid,
  programStartDate,
  credentialingHealthPlan,
}: PatientHistoryEntryProps) => (
  <ExpandableCard
    id={`patient-history-${entry.entryID}`}
    title={<Title entry={entry} uid={uid} />}
    subheader={<Subtitle entry={entry} programStartDate={programStartDate} />}
    subheaderTypographyProps={{ variant: 'caption' }}
  >
    <CardContent>
      <ViewOnlyForm initialValues={getInitialValuesFromPatientHistoryEntry(entry)}>
        {entry.type && PATIENT_HISTORY_TYPES_WITH_ONLY_DESCRIPTION_FIELD.has(entry.type) && <NoteFields />}
        {entry.type === PatientHistoryType.Accepted && <ParticipantAcceptedFields />}
        {entry.type === PatientHistoryType.Burnout && <BurnoutFields />}
        {entry.type === PatientHistoryType.Custom && <CustomFields />}
        {entry.type === PatientHistoryType.Declined && <ParticipantDeclinedFields />}
        {entry.type === PatientHistoryType.MissedIntakeCall && <MissedIntakeCallFields />}
        {entry.type === PatientHistoryType.IntakeNoteCoaching && <IntakeNoteCoachingFields />}
        {entry.type === PatientHistoryType.Phq9 && <PHQ9Fields />}
        {entry.type === PatientHistoryType.Gad7 && <GAD7Fields />}
        {entry.type === PatientHistoryType.Who5 && <WHO5Fields />}
        {entry.type === PatientHistoryType.Pcl5 && <PCL5Fields />}
        {entry.type === PatientHistoryType.Asrm && <ASRMFields />}
        {entry.type === PatientHistoryType.Ocir && <OCIRFields />}
        {entry.type === PatientHistoryType.Isi && <ISIFields />}
        {entry.type === PatientHistoryType.ProgramEnd && <ProgramEndFields />}
        {entry.type === PatientHistoryType.Wpai && <WPAIFields />}
        {entry.__typename === 'CommonPatientHistory' && entry.type === PatientHistoryType.ParticipantReEnrolled && (
          <ParticipantReEnrollmentTable value={entry.value ?? null} />
        )}
        {entry.__typename === 'VideoCallPatientHistory' && (
          <VideoCallPatientHistoryFields patientHistoryEntry={entry} />
        )}
        {entry.__typename === 'FinancialEligibilityPatientHistory' && (
          <FinancialEligibilityHealthPlanFields showCostEstimateDisclaimer={Boolean(entry.costEstimate)} uid={uid} />
        )}
        {entry.type === PatientHistoryType.IntakeNote && <IntakeNoteFields uid={uid} />}
        {entry.type === PatientHistoryType.TreatmentPlan && <TreatmentPlanFields viewOnly />}
        {entry.type === PatientHistoryType.IntakeNotePsychiatry && <IntakeNotePsychiatryFields viewOnly />}
        {entry.type === PatientHistoryType.LabOrder && <LabOrderFields />}
        {entry.type === PatientHistoryType.IntakeNoteComprehensive && <IntakeNoteComprehensiveFields viewOnly />}
        {(entry.type === PatientHistoryType.TreatmentPlanComprehensive ||
          entry.type === PatientHistoryType.TreatmentPlanComprehensiveUpdate) && (
          <TreatmentPlanComprehensiveFields viewOnly />
        )}
        {entry.type === PatientHistoryType.DischargeSummaryNote && (
          <DischargeSummaryNoteFields viewOnly credentialingHealthPlan={credentialingHealthPlan} />
        )}
        {entry.type === PatientHistoryType.MonthlySummaryNote && (
          <MonthlySummaryNoteFields viewOnly isKaiser={Boolean(checkIfKaiserHealthPlan(credentialingHealthPlan))} />
        )}
        {entry.type === PatientHistoryType.CheckInCall && (
          <CheckinCallNoteFields
            viewOnly
            memberIdLabel={checkIfKaiserHealthPlan(credentialingHealthPlan) ? 'Kaiser MRN' : 'Member ID'}
          />
        )}
        {entry.type === PatientHistoryType.AdvancedFollowUpNote && <AdvancedFollowUpNoteFields />}
        {entry.files && entry.files.length > 0 && <PatientHistoryFilesPreview files={entry.files} />}
      </ViewOnlyForm>
    </CardContent>
  </ExpandableCard>
)

export default ParticipantHistoryEntry
